import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowLeft, ShieldCheck, X, Check } from "lucide-react";

/**
 * <Ecosystem /> — the Memory Lane ecosystem / patient-journey visual.
 *
 * A single horizontal composition split into three bands:
 *   1. THE PROBLEM — fragmented, cross-organization dementia journey
 *   2. THE SOLUTION — Memory Lane persists across every stage
 *   3. THE RELATIONSHIP MODEL — org plugs in, caregiver owns the road
 *
 * Rendered as inline SVG so it's:
 *   • Print-perfect at any zoom (no bitmap fuzz)
 *   • Editable in Figma/Illustrator (paste the SVG source)
 *   • Screenshot-able for pitch decks
 *
 * We also expose a "Download SVG" button that serializes the DOM node
 * to a standalone .svg file — the founder can hand it to a designer,
 * a partner-org marketing team, or embed it in a slide deck.
 */

const CARE_PARTNERS = [
    { id: "primary",   label: "Primary Care",       tint: "#12776F" },
    { id: "neuro",     label: "Neurology",          tint: "#2B6CB0" },
    { id: "imaging",   label: "Imaging Center",     tint: "#6B46C1" },
    { id: "guide",     label: "GUIDE Program",      tint: "#DD6B20" },
    { id: "home",      label: "Home Health",        tint: "#3182CE" },
    { id: "community", label: "Community Resources", tint: "#805AD5" },
    { id: "legal",     label: "Legal / Financial",  tint: "#B7791F" },
    { id: "hospice",   label: "Hospice (if needed)", tint: "#C53030" },
];

const CAREGIVERS = [
    {
        id: "linda",
        name: "LINDA, 62",
        role: "Wife",
        stage: "Early concerns before neurology",
        quote: "I just want answers. What is happening to him?",
        color: "#12776F",
        // Which stages her Memory Lane journey touches (indexes into CARE_PARTNERS)
        active: [0],                    // just Primary Care so far
        recentEnd: -1,
    },
    {
        id: "marcus",
        name: "MARCUS, 45",
        role: "Son",
        stage: "Helping dad through diagnosis & workup",
        quote: "Do I really have to repeat all of this again?",
        color: "#2B6CB0",
        active: [0, 1, 2],              // PC → Neuro → Imaging
        recentEnd: -1,
    },
    {
        id: "sofia",
        name: "SOFIA, 58",
        role: "Daughter",
        stage: "Managing mom's dementia care day-to-day",
        quote: "Why does no one have the big picture?",
        color: "#B7791F",
        active: [3, 4, 5],              // GUIDE + Home Health + Community
        recentEnd: 2,                    // finished imaging
    },
    {
        id: "james",
        name: "JAMES, 50",
        role: "Husband",
        stage: "Advanced care & hospice planning",
        quote: "We're back to day one all over again.",
        color: "#C53030",
        active: [6, 7],                  // Legal + Hospice
        recentEnd: 4,                    // finished home health
    },
];

export default function Ecosystem() {
    const svgRef = useRef(null);

    const downloadSvg = () => {
        if (!svgRef.current) return;
        const clone = svgRef.current.cloneNode(true);
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        const src = new XMLSerializer().serializeToString(clone);
        const blob = new Blob(
            [`<?xml version="1.0" encoding="UTF-8"?>\n${src}`],
            { type: "image/svg+xml;charset=utf-8" }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "memory-lane-ecosystem.svg";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-cream-light" data-testid="ecosystem-page">
            {/* Header — hidden in exported SVG since it's outside the ref */}
            <header className="max-w-[1920px] mx-auto px-6 py-6 flex items-center justify-between">
                <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#4C5551] hover:text-teal-dark">
                    <ArrowLeft size={16} strokeWidth={1.8} /> Back to Memory Lane
                </Link>
                <div className="text-right">
                    <h1 className="text-lg font-heading font-semibold text-[#1F2624] leading-tight">
                        The Memory Lane ecosystem
                    </h1>
                    <p className="text-xs text-[#6E7A74]">
                        For investors, healthcare partners, and pitch decks.
                    </p>
                </div>
                <button
                    onClick={downloadSvg}
                    className="ml-btn-primary inline-flex items-center gap-1.5 text-sm"
                    data-testid="ecosystem-download-svg"
                >
                    <Download size={14} strokeWidth={2} /> Download SVG
                </button>
            </header>

            <div className="max-w-[1920px] mx-auto px-4 pb-16 overflow-x-auto">
                <svg
                    ref={svgRef}
                    viewBox="0 0 1920 2200"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ width: "100%", height: "auto", background: "#F6F1E7" }}
                    data-testid="ecosystem-svg"
                    aria-label="Memory Lane ecosystem map — fragmented care journey vs. Memory Lane's continuous road"
                >
                    <defs>
                        <marker id="arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto">
                            <path d="M0,0 L10,5 L0,10 z" fill="#12776F" />
                        </marker>
                        <linearGradient id="mlBar" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#134E4E" />
                            <stop offset="100%" stopColor="#12776F" />
                        </linearGradient>
                        <pattern id="grain" width="200" height="200" patternUnits="userSpaceOnUse">
                            <circle cx="1" cy="1" r="0.7" fill="rgba(0,0,0,0.02)" />
                        </pattern>
                    </defs>
                    <rect x="0" y="0" width="1920" height="2200" fill="url(#grain)" />

                    {/* ====================================================
                        BAND 1 — THE PROBLEM
                        ==================================================== */}
                    <ProblemBand />

                    {/* ====================================================
                        BAND 2 — THE SOLUTION
                        ==================================================== */}
                    <SolutionBand />

                    {/* ====================================================
                        BAND 3 — RELATIONSHIP MODEL
                        ==================================================== */}
                    <RelationshipFooter />
                </svg>

                <p className="text-xs text-[#6E7A74] mt-4 text-center italic">
                    Tip: click <strong>Download SVG</strong> to open in Figma, Illustrator, or PowerPoint. All shapes and text stay editable.
                </p>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* PROBLEM BAND                                                                */
/* -------------------------------------------------------------------------- */

function ProblemBand() {
    return (
        <g data-band="problem">
            {/* Band background */}
            <rect x="20" y="20" width="1880" height="920" rx="16" fill="#FFFFFF" stroke="#E2E8F0" />

            {/* Corner tag */}
            <rect x="40" y="40" width="180" height="34" rx="6" fill="#C53030" />
            <text x="130" y="63" textAnchor="middle" fill="#FFF" fontSize="14" fontWeight="700" letterSpacing="1.5">THE PROBLEM</text>

            {/* Title */}
            <text x="960" y="70" textAnchor="middle" fontSize="30" fontWeight="700" fill="#1F2624" fontFamily="'Playfair Display', serif">
                Caregivers get lost in a fragmented system
            </text>
            <text x="960" y="100" textAnchor="middle" fontSize="15" fill="#4C5551" fontStyle="italic">
                Multiple organizations. Multiple intakes. Repeating the same story over and over.
            </text>

            {/* Caregiver personas — left column */}
            <text x="60" y="145" fontSize="11" fontWeight="700" fill="#C53030" letterSpacing="1.5">REAL CAREGIVER EXAMPLES</text>
            {CAREGIVERS.map((c, i) => (
                <PersonaCard key={c.id} x={40} y={165 + i * 175} caregiver={c} tone="problem" />
            ))}

            {/* Care-partner boxes — top row across */}
            {CARE_PARTNERS.slice(0, 6).map((p, i) => (
                <CarePartnerBox
                    key={p.id}
                    x={330 + i * 210}
                    y={155}
                    index={i + 1}
                    label={p.label}
                    tint={p.tint}
                    bullets={["New intake", "Repeat history", "Different portal", "New paperwork"]}
                />
            ))}
            {/* Bottom row for GUIDE-not-eligible / Legal + Hospice */}
            <CarePartnerBox x={540} y={520} index={7} label="Legal / Financial" tint="#B7791F"
                bullets={["New intake", "Repeat history", "New paperwork", "Different portal"]} />
            <CarePartnerBox x={960} y={520} index={8} label="Hospice (if needed)" tint="#C53030"
                bullets={["New intake", "Repeat history", "New paperwork", "Different portal"]} />

            {/* Chaotic road tangle */}
            <ChaoticRoad />

            {/* Speech bubbles — the caregiver's real words */}
            <SpeechBubble x={355} y={410} text="I just told this story." />
            <SpeechBubble x={575} y={410} text="Start over again." tone="stop" />
            <SpeechBubble x={805} y={410} text="Start over again." tone="stop" />
            <SpeechBubble x={1030} y={410} text="Start over again." tone="stop" />
            <SpeechBubble x={1255} y={410} text="Start over again." tone="stop" />
            <SpeechBubble x={355} y={560} text="Do I need to repeat all of this again?" />
            <SpeechBubble x={355} y={700} text="Why does no one have the big picture?" />
            <SpeechBubble x={575} y={700} text={"We\u2019re back to day one\u2026 again."} />

            {/* STOP sign markers */}
            {[540, 760, 985, 1210, 1435].map((x, i) => (
                <StopMarker key={i} x={x} y={445} />
            ))}

            {/* Dead-end marker */}
            <DeadEnd x={1750} y={800} />

            {/* Right sidebar — WHAT HAPPENS TODAY */}
            <g transform="translate(1590, 145)">
                <rect x="0" y="0" width="290" height="720" rx="12" fill="#FEF5F5" stroke="#FEB2B2" />
                <text x="145" y="30" textAnchor="middle" fontSize="14" fontWeight="700" fill="#C53030" letterSpacing="1.5">WHAT HAPPENS TODAY</text>
                {[
                    "Repeat history at every stop",
                    "New intake, new portals",
                    "Caregiver assessment over and over",
                    "Different organizations don\u2019t share info",
                    "Caregivers feel lost, frustrated, exhausted",
                    "Care plans are delayed & disjointed",
                ].map((t, i) => (
                    <g key={i} transform={`translate(20, ${80 + i * 95})`}>
                        <circle cx="12" cy="12" r="12" fill="#C53030" />
                        <text x="12" y="17" textAnchor="middle" fill="#FFF" fontSize="14" fontWeight="700">×</text>
                        <foreignObject x="36" y="0" width="240" height="80">
                            <div xmlns="http://www.w3.org/1999/xhtml" style={{ font: "14px/1.4 -apple-system, sans-serif", color: "#742A2A" }}>{t}</div>
                        </foreignObject>
                    </g>
                ))}
            </g>
        </g>
    );
}

/* -------------------------------------------------------------------------- */
/* SOLUTION BAND                                                               */
/* -------------------------------------------------------------------------- */

function SolutionBand() {
    return (
        <g data-band="solution" transform="translate(0, 970)">
            <rect x="20" y="0" width="1880" height="960" rx="16" fill="#FFFFFF" stroke="#B2F5EA" />

            {/* Corner tag */}
            <rect x="40" y="30" width="180" height="34" rx="6" fill="#12776F" />
            <text x="130" y="53" textAnchor="middle" fill="#FFF" fontSize="14" fontWeight="700" letterSpacing="1.5">THE SOLUTION</text>

            <text x="960" y="60" textAnchor="middle" fontSize="30" fontWeight="700" fill="#1F2624" fontFamily="'Playfair Display', serif">
                Memory Lane stays with you across every organization
            </text>
            <text x="960" y="90" textAnchor="middle" fontSize="15" fill="#4C5551" fontStyle="italic">
                One account. One story. One shared journey.
            </text>

            {/* Memory Lane badge, left */}
            <g transform="translate(50, 130)">
                <rect width="240" height="220" rx="14" fill="#134E4E" />
                <text x="120" y="60" textAnchor="middle" fill="#FFF" fontSize="26" fontWeight="700" fontFamily="'Playfair Display', serif">Memory Lane</text>
                <text x="120" y="90" textAnchor="middle" fill="#B2F5EA" fontSize="11" letterSpacing="2">CARE NAVIGATOR</text>
                <line x1="60" y1="110" x2="180" y2="110" stroke="#12776F" strokeWidth="2" />
                <text x="120" y="140" textAnchor="middle" fill="#E6FFFA" fontSize="14" fontWeight="600">YOUR GUIDE.</text>
                <text x="120" y="160" textAnchor="middle" fill="#E6FFFA" fontSize="14" fontWeight="600">YOUR ROAD.</text>
                <text x="120" y="180" textAnchor="middle" fill="#E6FFFA" fontSize="14" fontWeight="600">EVERY STEP.</text>
            </g>

            {/* Care partners strip (8 across the top of the solution) */}
            {CARE_PARTNERS.map((p, i) => (
                <SolutionPartnerBox key={p.id} x={330 + i * 160} y={135} index={i + 1} label={p.label} tint={p.tint} />
            ))}

            {/* Central Memory Lane road bar */}
            <rect x="330" y="410" width="1265" height="60" rx="8" fill="url(#mlBar)" />
            <text x="962" y="445" textAnchor="middle" fill="#FFFFFF" fontSize="26" fontWeight="700" letterSpacing="3">
                MEMORY LANE
            </text>
            <text x="962" y="465" textAnchor="middle" fill="#B2F5EA" fontSize="11" fontStyle="italic">
                your continuous, always-with-you journey
            </text>

            {/* Vertical connectors from partners → Memory Lane bar */}
            {CARE_PARTNERS.map((p, i) => {
                const cx = 330 + i * 160 + 70;
                return (
                    <g key={p.id}>
                        <line x1={cx} y1="345" x2={cx} y2="410" stroke={p.tint} strokeWidth="2.5" strokeDasharray="6,4" />
                        <rect x={cx - 45} y="355" width="90" height="22" rx="6" fill={p.tint} />
                        <text x={cx} y="371" textAnchor="middle" fill="#FFF" fontSize="10" fontWeight="700" letterSpacing="0.5">CONNECTS HERE</text>
                        <text x={cx} y="395" textAnchor="middle" fill="#4C5551" fontSize="10">services active</text>
                    </g>
                );
            })}

            {/* Caregiver timeline rows */}
            <text x="60" y="510" fontSize="12" fontWeight="700" fill="#12776F" letterSpacing="1">YOU STAY ON</text>
            <text x="60" y="528" fontSize="12" fontWeight="700" fill="#12776F" letterSpacing="1">MEMORY LANE</text>
            <text x="60" y="546" fontSize="12" fontWeight="700" fill="#12776F" letterSpacing="1">CONTINUOUSLY</text>
            <text x="60" y="580" fontSize="11" fill="#4C5551">Before, during, between,</text>
            <text x="60" y="595" fontSize="11" fill="#4C5551">and after services.</text>
            <text x="60" y="625" fontSize="11" fill="#4C5551" fontStyle="italic">No need to start over.</text>

            {CAREGIVERS.map((c, rowIdx) => (
                <CaregiverTimelineRow key={c.id} y={510 + rowIdx * 80} caregiver={c} />
            ))}

            {/* Right sidebar — WHY MEMORY LANE WORKS */}
            <g transform="translate(1620, 130)">
                <rect x="0" y="0" width="260" height="720" rx="12" fill="#F0FFF4" stroke="#9AE6B4" />
                <text x="130" y="30" textAnchor="middle" fontSize="13" fontWeight="700" fill="#276749" letterSpacing="1.5">WHY MEMORY LANE WORKS</text>
                {[
                    "Caregiver owns the Memory Lane account",
                    "Information entered once",
                    "Shared with the right organization at the right time",
                    "Services can start & stop",
                    "Memory Lane stays before, during, between, and after services",
                    "No need to start over",
                ].map((t, i) => (
                    <g key={i} transform={`translate(20, ${70 + i * 100})`}>
                        <circle cx="12" cy="12" r="12" fill="#2F855A" />
                        <text x="12" y="17" textAnchor="middle" fill="#FFF" fontSize="16" fontWeight="700">✓</text>
                        <foreignObject x="36" y="0" width="210" height="90">
                            <div xmlns="http://www.w3.org/1999/xhtml" style={{ font: "14px/1.4 -apple-system, sans-serif", color: "#22543D" }}>{t}</div>
                        </foreignObject>
                    </g>
                ))}
            </g>
        </g>
    );
}

/* -------------------------------------------------------------------------- */
/* RELATIONSHIP FOOTER                                                         */
/* -------------------------------------------------------------------------- */

function RelationshipFooter() {
    return (
        <g transform="translate(0, 1960)">
            <rect x="20" y="0" width="1880" height="220" rx="16" fill="#134E4E" />

            <g transform="translate(50, 40)">
                <circle cx="20" cy="20" r="20" fill="#12776F" />
                <text x="20" y="26" textAnchor="middle" fill="#FFF" fontSize="18" fontWeight="700">◎</text>
                <text x="55" y="15" fill="#B2F5EA" fontSize="11" fontWeight="700" letterSpacing="2">THE</text>
                <text x="55" y="30" fill="#B2F5EA" fontSize="11" fontWeight="700" letterSpacing="2">RELATIONSHIP</text>
                <text x="55" y="45" fill="#B2F5EA" fontSize="11" fontWeight="700" letterSpacing="2">MODEL</text>
            </g>

            <g transform="translate(220, 40)">
                <text x="0" y="0" fontSize="13" fill="#E6FFFA" fontStyle="italic">Organizations do not own the patient journey.</text>
                <text x="0" y="30" fontSize="13" fill="#E6FFFA" fontStyle="italic">The caregiver/patient owns Memory Lane.</text>
                <text x="0" y="60" fontSize="13" fill="#E6FFFA" fontStyle="italic">Organizations plug into the journey when involved.</text>
            </g>

            <line x1="770" y1="40" x2="770" y2="170" stroke="#12776F" strokeWidth="1" />

            <g transform="translate(820, 40)">
                <text x="0" y="0" fontSize="18" fontWeight="700" fill="#FFF" letterSpacing="2">ALWAYS YOURS. ALWAYS INTACT.</text>
                <text x="0" y="30" fontSize="13" fill="#E6FFFA" fontStyle="italic">If a family leaves neurology, finishes home health, loses GUIDE eligibility,</text>
                <text x="0" y="50" fontSize="13" fill="#E6FFFA" fontStyle="italic">or later enters hospice, their Memory Lane history stays intact.</text>
            </g>

            <line x1="1400" y1="40" x2="1400" y2="170" stroke="#12776F" strokeWidth="1" />

            <g transform="translate(1440, 60)">
                <text x="0" y="0" fontSize="24" fill="#FFF" fontFamily="'Playfair Display', serif" fontStyle="italic">One road.</text>
                <text x="0" y="35" fontSize="24" fill="#FFF" fontFamily="'Playfair Display', serif" fontStyle="italic">One story.</text>
                <text x="0" y="70" fontSize="24" fill="#B2F5EA" fontFamily="'Playfair Display', serif" fontStyle="italic">Always with you.</text>
                <text x="0" y="105" fontSize="11" fill="#B2F5EA" letterSpacing="1">— MEMORY LANE CARE NAVIGATOR</text>
            </g>
        </g>
    );
}

/* -------------------------------------------------------------------------- */
/* Sub-components                                                              */
/* -------------------------------------------------------------------------- */

function PersonaCard({ x, y, caregiver, tone }) {
    const bg = tone === "problem" ? "#FFF" : "#F0FDFA";
    const border = tone === "problem" ? "#E2E8F0" : "#B2F5EA";
    return (
        <g transform={`translate(${x}, ${y})`}>
            <rect width="260" height="155" rx="10" fill={bg} stroke={border} />
            <circle cx="40" cy="42" r="24" fill={caregiver.color} />
            <text x="40" y="48" textAnchor="middle" fill="#FFF" fontSize="16" fontWeight="700">
                {caregiver.name.charAt(0)}
            </text>
            <text x="80" y="30" fontSize="13" fontWeight="700" fill="#1F2624">{caregiver.name}</text>
            <text x="80" y="45" fontSize="11" fill="#6E7A74">{caregiver.role}</text>
            <foreignObject x="15" y="80" width="230" height="60">
                <div xmlns="http://www.w3.org/1999/xhtml" style={{ font: "12px/1.35 -apple-system, sans-serif", color: "#4C5551" }}>{caregiver.stage}</div>
            </foreignObject>
        </g>
    );
}

function CarePartnerBox({ x, y, index, label, tint, bullets }) {
    return (
        <g transform={`translate(${x}, ${y})`}>
            <rect width="185" height="145" rx="8" fill="#FFF" stroke={tint} strokeWidth="2" />
            <circle cx="20" cy="22" r="12" fill={tint} />
            <text x="20" y="27" textAnchor="middle" fill="#FFF" fontSize="11" fontWeight="700">{index}</text>
            <text x="40" y="27" fontSize="12" fontWeight="700" fill={tint}>{label.toUpperCase()}</text>
            {bullets.map((b, i) => (
                <text key={i} x="15" y={55 + i * 18} fontSize="11" fill="#4C5551">• {b}</text>
            ))}
        </g>
    );
}

function SolutionPartnerBox({ x, y, index, label, tint }) {
    return (
        <g transform={`translate(${x}, ${y})`}>
            <rect width="140" height="80" rx="8" fill="#FFF" stroke={tint} strokeWidth="2" />
            <circle cx="18" cy="20" r="10" fill={tint} />
            <text x="18" y="24" textAnchor="middle" fill="#FFF" fontSize="10" fontWeight="700">{index}</text>
            <text x="35" y="24" fontSize="10" fontWeight="700" fill={tint}>{label.toUpperCase()}</text>
            <foreignObject x="10" y="35" width="125" height="40">
                <div xmlns="http://www.w3.org/1999/xhtml" style={{ font: "10px/1.3 -apple-system, sans-serif", color: "#4C5551", textAlign: "center" }}>Services active on demand</div>
            </foreignObject>
        </g>
    );
}

function ChaoticRoad() {
    // Multiple curving overlapping road paths to signal chaos
    return (
        <g stroke="#94A3B8" fill="none" strokeWidth="14" strokeLinecap="round" opacity="0.7">
            <path d="M330 450 Q450 350 570 460 T810 450 Q930 550 1050 450 T1290 450 Q1410 550 1530 450" />
            <path d="M330 500 Q470 610 610 500 T870 510 Q990 400 1110 510 T1370 500 Q1490 400 1590 490" strokeDasharray="4,10" />
            <path d="M330 620 Q450 720 580 620 T820 630 Q940 520 1060 630 T1300 620" strokeWidth="10" opacity="0.5" />
            <path d="M400 780 Q560 700 700 780 T950 780 Q1080 700 1200 780 T1460 780" strokeWidth="10" opacity="0.4" />
        </g>
    );
}

function SpeechBubble({ x, y, text, tone }) {
    const bg = tone === "stop" ? "#FEF3C7" : "#FFF";
    const stroke = tone === "stop" ? "#D69E2E" : "#CBD5E0";
    return (
        <g transform={`translate(${x}, ${y})`}>
            <rect width="140" height="42" rx="20" fill={bg} stroke={stroke} strokeWidth="1.5" />
            <foreignObject x="10" y="6" width="120" height="34">
                <div xmlns="http://www.w3.org/1999/xhtml" style={{ font: "11px/1.3 -apple-system, sans-serif", color: "#1F2624", textAlign: "center" }} dangerouslySetInnerHTML={{ __html: text }} />
            </foreignObject>
        </g>
    );
}

function StopMarker({ x, y }) {
    return (
        <g transform={`translate(${x}, ${y})`}>
            <polygon points="0,-14 10,-14 17,-7 17,7 10,14 0,14 -7,7 -7,-7" fill="#C53030" stroke="#FFF" strokeWidth="1.5" />
            <text x="5" y="4" textAnchor="middle" fill="#FFF" fontSize="9" fontWeight="700">STOP</text>
        </g>
    );
}

function DeadEnd({ x, y }) {
    return (
        <g transform={`translate(${x}, ${y})`}>
            <rect width="90" height="55" rx="4" fill="#DD6B20" stroke="#FFF" strokeWidth="2" />
            <text x="45" y="28" textAnchor="middle" fill="#FFF" fontSize="11" fontWeight="700">DEAD</text>
            <text x="45" y="44" textAnchor="middle" fill="#FFF" fontSize="11" fontWeight="700">END</text>
        </g>
    );
}

function CaregiverTimelineRow({ y, caregiver }) {
    // Draw a horizontal timeline segment for this caregiver, with
    // "STARTS HERE", "services active", "ENDS offboarded", and
    // "may reconnect later" tags based on their active[] array.
    return (
        <g transform={`translate(220, ${y})`}>
            {/* Caregiver name column */}
            <circle cx="20" cy="20" r="16" fill={caregiver.color} />
            <text x="20" y="25" textAnchor="middle" fill="#FFF" fontSize="12" fontWeight="700">{caregiver.name.charAt(0)}</text>
            <text x="45" y="15" fontSize="11" fontWeight="700" fill="#1F2624">{caregiver.name}</text>
            <text x="45" y="30" fontSize="10" fill="#4C5551">{caregiver.role}</text>
            <text x="45" y="44" fontSize="9" fill="#6E7A74" fontStyle="italic">{caregiver.stage}</text>

            {/* Continuous Memory Lane line for this caregiver */}
            <line x1="160" y1="20" x2="1355" y2="20" stroke={caregiver.color} strokeWidth="3" strokeDasharray="10,4" opacity="0.35" />
            <polygon points="1355,15 1370,20 1355,25" fill={caregiver.color} opacity="0.55" />

            {/* Stage-specific chips */}
            {CARE_PARTNERS.map((_, i) => {
                const cx = 110 + i * 160 + 40;
                const active = caregiver.active.includes(i);
                const ended = caregiver.recentEnd === i;
                if (active) {
                    return (
                        <g key={i} transform={`translate(${cx}, 8)`}>
                            <rect width="100" height="24" rx="4" fill={caregiver.color} />
                            <text x="50" y="16" textAnchor="middle" fill="#FFF" fontSize="10" fontWeight="700">STARTS HERE</text>
                        </g>
                    );
                }
                if (ended) {
                    return (
                        <g key={i} transform={`translate(${cx}, 8)`}>
                            <rect width="100" height="24" rx="4" fill="#FFF" stroke={caregiver.color} strokeWidth="1.5" />
                            <text x="50" y="16" textAnchor="middle" fill={caregiver.color} fontSize="10" fontWeight="600">ENDS offboarded</text>
                        </g>
                    );
                }
                return null;
            })}
        </g>
    );
}
